import { memo, useCallback, useMemo } from 'react';
import { List, OrderedMap } from 'immutable';
import styled from 'styled-components';
import I18n from 'I18n';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIResultsMessage from 'UIComponents/results/UIResultsMessage';
import UniversalAssociationsSelectOptions from './UniversalAssociationsSelectOptions';
import CampaignsEmptyState from './CampaignsEmptyState';
import { CAMPAIGNS_OBJECT_TYPE_ID } from 'universal-associations-select/hooks/useFetchHasCampaigns';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUIResultsMessage = styled(UIResultsMessage).withConfig({
  displayName: "UniversalAssociationsSelectSearchResults__StyledUIResultsMessage",
  componentId: "k63v4t-0"
})(["img{margin-bottom:16px;}"]);
const UniversalAssociationsSelectSearchResults = ({
  canEdit,
  emailRecipientsIds,
  engagementOrInteractionType,
  isSearching,
  onToggleSelectAssociation,
  readOnly,
  searchResults,
  searchText,
  selectedObjectTypeId,
  shouldDisableLastAssociation,
  OptionWrapperComponent,
  refetchOptions
}) => {
  const renderedEmptyState = useMemo(() => {
    if (isSearching) {
      return /*#__PURE__*/_jsx(StyledUIResultsMessage, {
        className: "m-top-2 m-bottom-0",
        illustration: "empty-state-charts",
        illustrationProps: {
          width: 130
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAssociationsSelect.noResultsFound"
        })
      });
    }
    if (selectedObjectTypeId === CAMPAIGNS_OBJECT_TYPE_ID) {
      return /*#__PURE__*/_jsx(CampaignsEmptyState, {
        refetchOptions: refetchOptions,
        engagementOrInteractionType: engagementOrInteractionType
      });
    }
    return /*#__PURE__*/_jsxs(StyledUIResultsMessage, {
      className: "m-top-2 m-bottom-0",
      illustration: "empty-state-charts",
      illustrationProps: {
        width: 130
      },
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: selectedObjectTypeId ? 'universalAssociationsSelect.searchResults.noResults.noRecordsFound' : 'universalAssociationsSelect.searchResults.noResults.noCurrentAssociations'
      }), /*#__PURE__*/_jsx("br", {}), selectedObjectTypeId ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.searchResults.noResults.searchToFind"
      }) : null]
    });
  }, [isSearching, selectedObjectTypeId, refetchOptions, engagementOrInteractionType]);
  const formatSearchResults = useCallback(data => {
    const contactsSection = data.find(associationRecord => (associationRecord === null || associationRecord === void 0 ? void 0 : associationRecord.get('toObjectTypeId')) === CONTACT_TYPE_ID);
    if (contactsSection) {
      const filteredOptions = contactsSection.get('associationOptionRecords').filter(option => !!(option !== null && option !== void 0 && option.get('isNewContact')));
      if (filteredOptions.size > 0) {
        data = data.withMutations(map => {
          map.forEach((val, key) => {
            if (val.get('toObjectTypeId') === CONTACT_TYPE_ID) {
              const nonNewContactOptions = val.get('associationOptionRecords').filter(option => !option.get('isNewContact'));
              if (nonNewContactOptions.size === 0) {
                map.delete(key);
              } else {
                map.set(key, val.set('associationOptionRecords', OrderedMap(nonNewContactOptions)));
              }
            }
          });
        });
        const updatedNewContactsSection = contactsSection.set('associationOptionRecords', OrderedMap(filteredOptions)).set('pluralObjectName', I18n.text('universalAssociationsSelect.searchResults.newContacts'));
        data = OrderedMap(Object.assign({
          'new-contacts-section': updatedNewContactsSection
        }, data.toObject()));
      }
    }
    return data;
  }, []);
  const renderedAssociationOptions = useMemo(() => {
    if (!searchResults) {
      return List();
    }
    const formattedSearchResults = formatSearchResults(searchResults);
    return formattedSearchResults.reduce((acc, associationRecord) => {
      /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
      return acc.concat( /*#__PURE__*/_jsx(UniversalAssociationsSelectOptions, {
        associationRecord: associationRecord,
        canEdit: canEdit,
        emailRecipientsIds: emailRecipientsIds,
        engagementOrInteractionType: engagementOrInteractionType,
        onToggleSelectAssociation: onToggleSelectAssociation,
        readOnly: readOnly,
        searchText: searchText,
        selectedObjectTypeId: selectedObjectTypeId,
        shouldDisableLastAssociation: shouldDisableLastAssociation,
        OptionWrapperComponent: OptionWrapperComponent
      }, `${associationRecord.get('pluralObjectName')}-associations-options`));
    }, List());
  }, [canEdit, emailRecipientsIds, engagementOrInteractionType, onToggleSelectAssociation, readOnly, searchResults, searchText, selectedObjectTypeId, shouldDisableLastAssociation, OptionWrapperComponent, formatSearchResults]);

  // Ensures a UniversalAssociationRecord exists for all associations search
  // and that object-specific record is populated with options
  if (!searchResults || !searchResults.size || !searchResults.first().get('associationOptionRecords').size) {
    return renderedEmptyState;
  }
  return renderedAssociationOptions;
};
export default /*#__PURE__*/memo(UniversalAssociationsSelectSearchResults);

// for testing purposes
const UniversalAssociationsSelectSearchResultsWrapper = {
  default: () => UniversalAssociationsSelectSearchResults
};
export { UniversalAssociationsSelectSearchResultsWrapper };